import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Building2, Store, Save, Video } from "lucide-react";
import { toast, Toaster } from "sonner";

const ROLE_BADGE = {
  admin: { color: "#D4AF37", label: "Admin" },
  staff: { color: "#7BA585", label: "Staff" },
  accountant: { color: "#9D8FB8", label: "Accountant" },
  sales: { color: "#C99356", label: "Sales" },
};

export default function UserWarehouses() {
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    const { data } = await api.get("/auth/users");
    setUsers(data);
    // Drafts: for warehouse-scoped users use their pin; for unscoped admins
    // (default_warehouse === null) preload "ALL" so the dropdown shows the
    // unscoped state up front.
    setDrafts(Object.fromEntries(
      data.map((u) => [
        u.id,
        u.default_warehouse || (u.role === "admin" ? "ALL" : "ONLINE_SALE"),
      ]),
    ));
  };
  useEffect(() => { load(); }, []);

  const save = async (uid) => {
    setSaving((s) => ({ ...s, [uid]: true }));
    try {
      await api.patch(`/auth/users/${uid}/warehouse`, { default_warehouse: drafts[uid] });
      toast.success("Warehouse updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving((s) => ({ ...s, [uid]: false }));
    }
  };

  return (
    <Layout
      title="User Warehouses"
      subtitle="Role-based default warehouse assignments"
    >
      <Toaster theme="dark" position="top-right" />
      <div className="glass-card p-6 mb-6 text-xs text-[#8A8A8A] leading-relaxed">
        <div className="overline mb-2 text-[#D4AF37]">How it works</div>
        Each user is locked to their assigned warehouse: inventory, sales, and dashboards
        only show that warehouse. <span className="text-[#D4AF37]">Admins</span> can be
        set to a specific warehouse (1 admin per warehouse) or left as
        <span className="text-[#D4AF37]"> "All Warehouses"</span> to keep unrestricted access.
        <span className="text-[#D4AF37]"> Super Admin</span> always sees everything and
        cannot be scoped. Updates take effect the next time the user reloads the page.
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="sticky top-0 z-10 bg-[#0F0F0F]">
              <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                <th className="px-4 sm:px-6 py-5 font-normal">User</th>
                <th className="px-4 sm:px-6 py-5 font-normal">Role</th>
                <th className="px-4 sm:px-6 py-5 font-normal">Default Warehouse</th>
                <th className="px-4 sm:px-6 py-5 font-normal hidden md:table-cell">Accessible</th>
                <th className="px-4 sm:px-6 py-5"></th>
              </tr>
            </thead>
            <tbody data-testid="user-warehouses-table">
              {users.map((u) => {
                const badge = ROLE_BADGE[u.role] || ROLE_BADGE.staff;
                // Super admin is always fully unrestricted — cannot be pinned.
                const isSuperAdmin = !!u.is_super_admin;
                const isAdmin = u.role === "admin";
                const draft = drafts[u.id] || (isAdmin ? "ALL" : "ONLINE_SALE");
                // Compare drafts to the source of truth. For an admin the
                // stored value can be null (unscoped) — represented as "ALL"
                // in the dropdown.
                const currentValue = u.default_warehouse || (isAdmin ? "ALL" : "ONLINE_SALE");
                const dirty = !isSuperAdmin && draft !== currentValue;
                return (
                  <tr key={u.id} className="border-b border-white/5">
                    <td className="px-4 sm:px-6 py-4">
                      <div className="serif">{u.name}</div>
                      <div className="text-[0.65rem] mono text-[#666] mt-0.5 truncate max-w-[180px]">{u.email}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <span
                        className="px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.16em] border whitespace-nowrap"
                        style={{ borderColor: badge.color + "55", color: badge.color, backgroundColor: badge.color + "10" }}
                      >
                        {isSuperAdmin ? "Super Admin" : badge.label}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      {isSuperAdmin ? (
                        <span className="text-[#8A8A8A] italic text-xs">— Access all (locked) —</span>
                      ) : (
                        <select
                          value={draft}
                          onChange={(e) => setDrafts((d) => ({ ...d, [u.id]: e.target.value }))}
                          className="luxe-input px-3 py-1.5 text-sm"
                          data-testid={`wh-select-${u.id}`}
                        >
                          {isAdmin && (
                            <option value="ALL">All Warehouses (unscoped)</option>
                          )}
                          <option value="ONLINE_SALE">Online Sale Inventory</option>
                          <option value="SHOWROOM">Showroom Display</option>
                          <option value="LIVE_PRODUCT">Live Product Stock</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-[0.7rem] mono text-[#8A8A8A] hidden md:table-cell">
                      {(u.accessible_warehouses || []).map((w) => {
                        const wl = { ONLINE_SALE: "Online", SHOWROOM: "Showroom", LIVE_PRODUCT: "Live" };
                        const Icon = w === "SHOWROOM" ? Store : w === "LIVE_PRODUCT" ? Video : Building2;
                        const color = w === "SHOWROOM" ? "text-[#7BA585]" : w === "LIVE_PRODUCT" ? "text-[#C99356]" : "text-[#D4AF37]";
                        return (
                          <span key={w} className="inline-flex items-center gap-1.5 mr-2">
                            <Icon className={`w-3 h-3 ${color}`} />
                            {wl[w] || w}
                          </span>
                        );
                      })}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-right">
                      {!isSuperAdmin && dirty && (
                        <button
                          onClick={() => save(u.id)}
                          disabled={!!saving[u.id]}
                          data-testid={`save-wh-${u.id}`}
                          className="btn-gold min-h-[44px] sm:min-h-0 px-4 py-1.5 inline-flex items-center gap-1.5 text-xs"
                        >
                          <Save className="w-3 h-3" /> {saving[u.id] ? "Saving…" : "Save"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
